import { getAllLanguagesInfo } from "@lingoshade/domain";
import { SettingsOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Slider,
  FormControlLabel,
  Switch,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../store";
import { saveLearningGoal } from "../../store/slices/learningSlice";

import type { LearningDay, LearningHome } from "@lingoshade/domain";

function totals(days: LearningDay[]): {
  active: number;
  minutes: number;
  words: number;
  dictations: number;
} {
  return {
    active: days.filter((day): boolean => day.qualifies).length,
    minutes: Math.ceil(
      days.reduce((sum, day): number => sum + day.listeningSeconds, 0) / 60,
    ),
    words: days.reduce((sum, day): number => sum + day.reviewedWords, 0),
    dictations: days.reduce(
      (sum, day): number => sum + day.completedDictations,
      0,
    ),
  };
}

export function LearningWeekSummary({
  home,
}: {
  home: LearningHome;
}): React.ReactElement {
  const { t } = useTranslation("home");
  const dispatch = useAppDispatch();
  const language = useAppSelector(
    (state): string => state.auth.user?.targetLanguage ?? "",
  );
  const languageName =
    getAllLanguagesInfo().find((entry): boolean => entry.code === language)
      ?.nativeName ?? language.toUpperCase();
  const saving = useAppSelector((state): boolean => state.learning.goalSaving);
  const error = useAppSelector((state): boolean => state.learning.goalError);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(home.weeklyGoalDays ?? 0);
  const week = totals(
    (home.activity ?? []).filter((day): boolean => day.date >= home.weekStart),
  );
  const goal = home.weeklyGoalDays;
  const save = async (): Promise<void> => {
    const result = await dispatch(saveLearningGoal(draft || null));
    if (saveLearningGoal.fulfilled.match(result)) setOpen(false);
  };
  return (
    <Paper data-testid="weekly-summary" elevation={0} sx={{ p: 2, mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {t("week.language", { language: languageName })}
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={600}>
          {goal
            ? t("week.goalProgress", { count: week.active, goal })
            : t("week.activeDays", { count: week.active })}
        </Typography>
        <IconButton
          aria-label={t("week.editGoal")}
          onClick={(): void => {
            setDraft(goal ?? 0);
            setOpen(true);
          }}
        >
          <SettingsOutlined />
        </IconButton>
      </Stack>
      {goal && (
        <LinearProgress
          aria-label={t("week.goalProgress", { count: week.active, goal })}
          variant="determinate"
          value={Math.min(100, (week.active / goal) * 100)}
          sx={{ mb: 1 }}
        />
      )}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
      >
        <Typography variant="body2">
          {t("week.listening", { count: week.minutes })}
        </Typography>
        <Typography variant="body2">
          {t("week.reviews", { count: week.words })}
        </Typography>
        <Typography variant="body2">
          {t("week.dictations", { count: week.dictations })}
        </Typography>
      </Box>
      <Dialog
        open={open}
        onClose={(): void => {
          if (!saving) setOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("week.editGoal")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t("week.goalDescription")}</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={draft > 0}
                disabled={saving}
                onChange={(_, checked): void => setDraft(checked ? 3 : 0)}
              />
            }
            label={t("week.editGoal")}
          />
          {draft > 0 && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography id="weekly-days-label">
                {t("week.goalDays")}: {draft}
              </Typography>
              <Slider
                aria-labelledby="weekly-days-label"
                min={1}
                max={7}
                step={1}
                marks={[1, 2, 3, 4, 5, 6, 7].map(
                  (value): { value: number; label: string } => ({
                    value,
                    label: String(value),
                  }),
                )}
                value={draft}
                disabled={saving}
                onChange={(_, value): void => setDraft(Number(value))}
              />
            </Box>
          )}
          {draft === 0 && (
            <Typography color="text.secondary">{t("week.noGoal")}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t("week.criteria")}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("week.saveError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={(): void => setOpen(false)}>
            {t("week.cancel")}
          </Button>
          <Button
            disabled={saving}
            variant="contained"
            onClick={(): void => {
              void save();
            }}
          >
            {t("week.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export function LearningActivityChart({
  home,
}: {
  home: LearningHome;
}): React.ReactElement {
  const { t, i18n } = useTranslation("home");
  const [period, setPeriod] = useState(7);
  const [selected, setSelected] = useState<string | null>(null);
  const activity = home.activity ?? [];
  const days =
    period === 7
      ? activity.filter((entry): boolean => entry.date >= home.weekStart)
      : activity.slice(-period);
  const day =
    days.find((entry): boolean => entry.date === selected) ??
    days[days.length - 1];
  const peak = Math.max(
    60,
    ...days.map((entry): number => entry.listeningSeconds),
  );
  const formatDate = (value: string): string =>
    new Intl.DateTimeFormat(i18n.language, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(new Date(`${value}T12:00:00Z`));
  if (!day)
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography>{t("firstPractice")}</Typography>
      </Paper>
    );
  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mt: 3 }} data-testid="activity-chart">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h6">{t("week.effort")}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={period}
          onChange={(_, value: number | null): void => {
            if (value) setPeriod(value);
          }}
          aria-label={t("week.period")}
        >
          <ToggleButton value={7}>{t("week.sevenDays")}</ToggleButton>
          <ToggleButton value={28}>{t("week.fourWeeks")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("week.chartDescription")}
      </Typography>
      <Box
        component="svg"
        viewBox="0 0 560 130"
        role="img"
        aria-label={t("week.effort")}
        sx={{ width: "100%", display: "block", mt: 2 }}
      >
        <line
          x1="0"
          x2="560"
          y1="105"
          y2="105"
          stroke="currentColor"
          opacity=".2"
        />
        {days.map((entry, index): React.ReactElement => {
          const width = 560 / days.length;
          const height = (entry.listeningSeconds / peak) * 95;
          return (
            <g key={entry.date}>
              <title>{`${formatDate(entry.date)}: ${t("week.listening", { count: Math.ceil(entry.listeningSeconds / 60) })}`}</title>
              <rect
                x={index * width + 3}
                y={105 - height}
                width={width - 6}
                height={height}
                fill={entry.date === day.date ? "#234e64" : "#83a5b4"}
                rx="2"
              />
              {(entry.reviewedWords > 0 || entry.completedDictations > 0) && (
                <circle
                  cx={(index + 0.5) * width}
                  cy="118"
                  r="3"
                  fill="#397259"
                />
              )}
            </g>
          );
        })}
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption">{formatDate(days[0].date)}</Typography>
        <Typography variant="caption">
          {formatDate(days[days.length - 1].date)}
        </Typography>
      </Stack>
      <Box
        component="input"
        type="range"
        min={0}
        max={days.length - 1}
        value={days.indexOf(day)}
        aria-label={t("week.selectDay")}
        aria-valuetext={formatDate(day.date)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          setSelected(days[Number(event.target.value)].date)
        }
        sx={{
          width: "100%",
          minHeight: 44,
          mx: 0,
          accentColor: "#234e64",
        }}
      />
      <Typography fontWeight={600}>{formatDate(day.date)}</Typography>
      <Typography variant="body2" aria-live="polite">
        {t("week.dayDetails", {
          minutes: Math.ceil(day.listeningSeconds / 60),
          words: day.reviewedWords,
          dictations: day.completedDictations,
        })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t("week.criteria")}
      </Typography>
    </Paper>
  );
}
